import React from "react";
import { connect } from "react-redux";
import { Dropdown, Grid, Loader, Dimmer, Button } from "semantic-ui-react";
import DownloadButton from "@buttons/DownloadButton";
import {
  getNoteAcceptorModelsRaw,
  loadNoteAcceptorModel,
  updateNoteAcceptorModel,
} from "@actions/index";
import { getAssetTable, getAssetsDetailsDownload, noteAcceptorDownload } from "@api/egm";
import { setCreateAssetModal, showSuccessAlert } from "@actions/modals";
import AssetMainTable from "./AssetMainTable";
import BaseSettings from "../systemAdmin/BaseSettings";
import OptionsList from "../systemAdmin/OptionsList";
import AssetDetailView from "./AssetDetailView";
import { ASSET_STATUS_FILTER_DATA } from "@helpers/constant";
import { handleDownload } from "@helpers/downloads";

const NOTE_ACCEPTOR_TABLE_COLUMNS = [
  { key: "name", label: "Name", accessor: "noteAcceptorName" },
  {
    key: "installed",
    label: "Installed",
    accessor: (row) => row.installedCount ?? "-",
  },
  {
    key: "notInstalled",
    label: "Not Installed",
    accessor: (row) => row.notInstalledCount ?? "-",
  },
  { key: "pendingScrapped", label: "Pending Scrapped", accessor: "pendingScrappedCount" },
  { key: "sold", label: "Sold", accessor: "soldCount" },
];

class NoteAcceptorView extends BaseSettings {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      operatorId: null,
      noteAcceptors: [],
      loading: false,
      saving: false,
      selectedNoteAcceptor: null,
      selectedOptions: {
        main: [],
        ownership: [],
        operational: [],
      },
      selectedNode: null,
      grid: null,
      doQuestion: false,
      doDelete: false,
      questionText: "",
      changed: [],
      showDetail: false,
      assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
      assetDetailAssets: [],
    };
  }

  componentDidUpdate(prevProps) {
    const { createAssetModal } = this.props;
    const prev = prevProps.createAssetModal ?? {};
    if (
      prev.open &&
      !createAssetModal?.open &&
      createAssetModal?.lastCreatedType === "NA"
    ) {
      this.getNoteAcceptors();
      this.props.setCreateAssetModal({ lastCreatedType: null });
    }
  }

  componentDidMount() {
    const { operators } = this.props;
    if (operators.length === 1 && operators[0].value !== -1) {
      this.setState(
        { operatorId: operators[0].value },
        this.getNoteAcceptors,
      );
    }
  }

  onOperatorChange = (e, { value }) => {
    this.setState(
      {
        operatorId: value,
        selectedNoteAcceptor: null,
        assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
        assetDetailAssets: [],
      },
      () => {
        if (value) {
          this.getNoteAcceptors();
        }
      },
    );
  };

  getAssetTableData = (noteAcceptorId, statusOption) => {
    const { operatorId } = this.state;

    return getAssetTable(
      operatorId,
      -1,
      -1,
      -1,
      -1,
      noteAcceptorId,
      statusOption,
      -1,
      0,
      "#",
      -1,
      -1,
      -1,
      true,
    );
  };

  handleAssetStatusOptionChange = (option) => {
    const { selectedNoteAcceptor } = this.state;
    if (!selectedNoteAcceptor) return;

    this.setState(
      { assetDetailStatusOption: option, loading: true },
      () => {
        this.getAssetTableData(selectedNoteAcceptor.noteAcceptorId, option)
          .then((tableData) => {
            this.setState({
              assetDetailAssets: tableData.assets || [],
              loading: false,
            });
          })
          .catch(() => this.setState({ loading: false }));
      },
    );
  };

  getNoteAcceptors = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      getNoteAcceptorModelsRaw(operatorId)
        .then((data) => {
          this.setState({
            noteAcceptors: Array.isArray(data) ? data : [],
            loading: false,
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  handleNew = (selectedOptions, selectedNode, grid = null) => {
    this.setState({
      selectedOptions: selectedOptions.main
        ? selectedOptions
        : { main: selectedOptions, ownership: [], operational: [] },
      changed: [],
      selectedNode,
      grid,
    });
  };

  onSelectRow = (row) => {
    if (!row) {
      this.setState({
        selectedNoteAcceptor: null,
        assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
        assetDetailAssets: [],
      });
      return;
    }

    const defaultStatus = ASSET_STATUS_FILTER_DATA[0].value;
    this.setState({
      loading: true,
      showDetail: true,
      assetDetailStatusOption: defaultStatus,
    });
    Promise.all([
      loadNoteAcceptorModel(row.noteAcceptorId),
      this.getAssetTableData(row.noteAcceptorId, defaultStatus),
    ])
      .then(([response, tableData]) => {
        const { doQuestion, doDelete, questionText } = response;
        this.setState(
          {
            doQuestion,
            doDelete,
            questionText,
            selectedNoteAcceptor: row,
            assetDetailAssets: tableData.assets || [],
          },
          () => {
            this.handleNew(
              response.assetUI
                ? response.assetUI
                : {
                    main: response.options || response.main || [],
                    operational: [],
                    ownership: [],
                  },
              row,
              response.grid,
            );
            this.setState({ loading: false });
          },
        );
      })
      .catch(() =>
        this.setState({ loading: false }),
      );
  };

  onNew = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.props.setCreateAssetModal({
      open: true,
      type: "NA",
      operatorId,
      name: "New Note Acceptor",
    });
  };

  onSave = () => {
    const { operatorId, selectedNoteAcceptor, selectedOptions } = this.state;
    const { showSuccessAlert } = this.props;
    if (!operatorId || !selectedNoteAcceptor) return;
    const noteAcceptorId = selectedNoteAcceptor
      ? selectedNoteAcceptor.noteAcceptorId
      : 0;
    const changed = this.getAll(selectedOptions);

    this.setState({ saving: true }, () => {
      updateNoteAcceptorModel(
        noteAcceptorId,
        operatorId,
        changed,
        () => this.getNoteAcceptors(),
      )
        .then(() => {
          this.setState({ saving: false }, () => {
            showSuccessAlert(
              noteAcceptorId
                ? "Note acceptor updated successfully."
                : "Note acceptor created successfully.",
            );
          });
        })
        .catch(() => this.setState({ saving: false }));
    });
  };

  handleDownload = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      noteAcceptorDownload(operatorId).then(
        (response) => {
          this.setState({ loading: false }, () =>
            handleDownload(response),
          );
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  handleAssetDetailDownload = () => {
    const {
      operatorId,
      selectedNoteAcceptor,
      assetDetailStatusOption,
    } = this.state;
    if (!operatorId || !selectedNoteAcceptor) return;

    this.setState({ loading: true }, () => {
      getAssetsDetailsDownload(
        operatorId,
        -1,
        -1,
        -1,
        -1,
        selectedNoteAcceptor.noteAcceptorId,
        assetDetailStatusOption,
        -1,
        0,
        "#",
        -1,
        -1,
        -1,
        true,
      ).then(
        (response) => {
          this.setState({ loading: false }, () => handleDownload(response));
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  render() {
    const {
      operatorId,
      noteAcceptors,
      loading,
      saving,
      selectedNoteAcceptor,
      selectedOptions,
      changed,
    } = this.state;

    const canSave =
      !!selectedNoteAcceptor &&
      this.isAllValid(
        selectedOptions || { main: [], ownership: [], operational: [] },
      ) &&
      changed.length > 0;

    return (
      <div className="flex-stretch gs-flex-container-vertical height-100">
        <Grid className="height-100 flex-stretch">
          <Grid.Row
            className={`flex-stretch ${this.state.showDetail ? "h-50" : "h-100"}`}
          >
            <Grid.Column width={12} className="height-100 flex">
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
              <div className="mb-0_5">
                <Dropdown
                  placeholder="Select operator"
                  selection
                  options={this.props.operators}
                  value={operatorId || ""}
                  onChange={this.onOperatorChange}
                  disabled={!this.props.operators.length}
                />
                <Button
                  className="ml-0_5"
                  icon="expand"
                  basic={!this.state.showDetail}
                  color="grey"
                  secondary
                  onClick={() =>
                    this.setState({ showDetail: !this.state.showDetail })
                  }
                  size="mini"
                  disabled={!selectedNoteAcceptor}
                />
              </div>
              <div className="mb-0_5 flex-stretch">
                <AssetMainTable
                  columns={NOTE_ACCEPTOR_TABLE_COLUMNS}
                  data={noteAcceptors}
                  rowKey="noteAcceptorId"
                  selectedRowId={selectedNoteAcceptor?.noteAcceptorId}
                  onSelectRow={this.onSelectRow}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={4} className="height-100 flex">
              <div className="flex-start">
                <Button
                  color="green"
                  content="New"
                  onClick={this.onNew}
                  disabled={!operatorId}
                  loading={loading}
                />
                <Button
                  primary
                  content="Save"
                  onClick={this.onSave}
                  disabled={!canSave}
                  loading={saving}
                />
                <DownloadButton
                  disabled={!this.state.operatorId}
                  caption="Export"
                  onClick={this.handleDownload}
                  loading={loading}
                />
              </div>
              <div className="flex-stretch height-100 p-1 border-grey-1 flex">
                <OptionsList
                  options={this.mapChanged(
                    (selectedOptions && selectedOptions.main) || [],
                  )}
                  onChange={this.handleChangeValue}
                />
              </div>
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
            </Grid.Column>
          </Grid.Row>
          {this.state.showDetail && (
            <Grid.Row className="flex-stretch h-50">
              <Grid.Column width={16}>
                <AssetDetailView
                  statusOption={this.state.assetDetailStatusOption}
                  assets={this.state.assetDetailAssets}
                  loading={this.state.loading}
                  operatorId={this.state.operatorId}
                  detailExportDisabled={!this.state.selectedNoteAcceptor}
                  onStatusOptionChange={this.handleAssetStatusOptionChange}
                  onDownloadClick={this.handleAssetDetailDownload}
                />
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  operators: state.operators?.filter((x) => x.value !== -1) ?? [],
  createAssetModal: state.createAssetModal,
});

export default connect(mapStateToProps, {
  setCreateAssetModal,
  showSuccessAlert,
})(NoteAcceptorView);